import { localDateString } from '../../../../../utils/helpers/localDate';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Slide } from '@mui/material';
import { Icon } from '@iconify/react';
import { updateOccupant, getOccupantDetails } from '../../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { getFlatsDropdown } from '../../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import SearchableDropdown from '../../../../../components/SearchableDropdown';
import { GENDER_OPTIONS, ENTITY_TYPE_OPTIONS, gstinError } from '../ownerFormOptions';
import MemberRoleSelect from '../../../../../components/MemberRoleSelect';
import usePermissions from '../../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../../consts/permissions';
import { selectedSocietyStorer } from '../../../../../utils/webStorer';
import fetchCurrentRoleId from '../../../../../utils/helpers/fetchCurrentRole';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const EditOwnerModal = ({
    open,
    handleClose,
    member,
    wings = [],
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const { updateLoading, occupantDetails, detailsLoading } = useSelector(state => state.OccupantReducer);
    const { flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);
    const { can } = usePermissions();
    // Anyone who may add / edit members picks the login role; the options
    // (and the API) limit non-admins to member roles.
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES) || can(PERMISSIONS.OCCUPANT_UPDATE);

    const modalFlats = flatsDropdown || [];

    const [formData, setFormData] = useState({
        entity_type: 'I',
        first_name: '',
        last_name: '',
        company_name: '',
        gender: '',
        date_of_birth: '',
        email: '',
        secondary_email: '',
        phone: '',
        secondary_phone: '',
        pan_no: '',
        gstin_no: '',
        address: '',
        billing_address: '',
        shipping_address: '',
        same_as_billing: false,
        wing_id: 'all',
        flat_id: 'all',
        ownership_share: '',
        start_date: '',
        role_id: '',
    });
    const [errors, setErrors] = useState({});

    // Fetch occupant details when modal opens
    useEffect(() => {
        if (member && open) {
            dispatch(getOccupantDetails({ id: member.flat_ownership_id || member.occupant_id, type: 'owner' }));
        }
        setErrors({});
    }, [member, open, dispatch]);

    // Populate form when details are fetched (owner detail has nested entity/flat)
    useEffect(() => {
        if (occupantDetails && open) {
            const details = occupantDetails;
            const entity = details.entity || details;
            const flat = details.flat || {};
            const billing = entity.billing_address || details.billing_address || '';
            const shipping = entity.shipping_address || details.shipping_address || '';
            setFormData((prev) => ({
                ...prev,
                entity_type: entity.entity_type || 'I',
                first_name: entity.first_name || '',
                last_name: entity.last_name || '',
                company_name: entity.company_name || '',
                gender: entity.gender || '',
                date_of_birth: entity.date_of_birth ? entity.date_of_birth.split('T')[0] : '',
                email: entity.email || '',
                secondary_email: entity.secondary_email || '',
                phone: entity.phone || '',
                secondary_phone: entity.secondary_phone || '',
                pan_no: entity.pan_no || details.pan_no || '',
                gstin_no: entity.gstin_no || entity.gst_no || details.gstin_no || '',
                address: entity.address || details.address || '',
                billing_address: billing,
                shipping_address: shipping,
                same_as_billing: !!billing && billing === shipping,
                wing_id: flat.wing?.wing_id || flat.wing_id || 'all',
                flat_id: flat.flat_id || details.flat_id || 'all',
                ownership_share: details.ownership_share ?? '',
                start_date: details.start_date ? details.start_date.split('T')[0] : '',
            }));
            const wingId = details.flat?.wing?.wing_id || details.flat?.wing_id;
            if (wingId) {
                dispatch(getFlatsDropdown({ wing_id: wingId }));
            }
        }
    }, [occupantDetails, open, dispatch]);

    const today = localDateString();
    // Preselect the owner's current login role from user-societies.
    useEffect(() => {
        if (!open || !canPickRole || !member) return;
        const ownershipId = member.flat_ownership_id || member.occupant_id;
        const societyId = selectedSocietyStorer.getSocietyId();
        let cancelled = false;
        fetchCurrentRoleId({
            societyId,
            sourceType: 'OWNERSHIP',
            sourceRowId: ownershipId,
        }).then((roleId) => {
            if (!cancelled && roleId) {
                setFormData((prev) => ({ ...prev, role_id: roleId }));
            }
        });
        return () => { cancelled = true; };
    }, [open, canPickRole, member]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
            if (name === 'billing_address' && prev.same_as_billing) {
                next.shipping_address = value;
            }
            if (name === 'same_as_billing' && checked) {
                next.shipping_address = prev.billing_address;
            }
            return next;
        });
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleWingChange = (value) => {
        setFormData(prev => ({ ...prev, wing_id: value, flat_id: 'all' }));
        if (value !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: value }));
        } else {
            dispatch(getFlatsDropdown({}));
        }
    };

    const handleFlatChange = (value) => {
        setFormData(prev => ({ ...prev, flat_id: value }));
    };

    const handleDropdownChange = (name) => (value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };
    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role_id: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        const isIndividual = formData.entity_type === 'I';
        if (isIndividual) {
            if (!formData.first_name.trim()) newErrors.first_name = 'First Name is required';
            if (formData.date_of_birth && formData.date_of_birth > today) {
                newErrors.date_of_birth = 'Date of birth cannot be in the future';
            }
        } else {
            if (!formData.company_name.trim()) newErrors.company_name = 'Company Name is required';
        }
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        } else if (!/^\d{10}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Phone must be exactly 10 digits';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (formData.secondary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secondary_email)) {
            newErrors.secondary_email = 'Invalid email format';
        }
        if (formData.secondary_phone && formData.secondary_phone.length > 15) {
            newErrors.secondary_phone = 'Phone must be at most 15 characters';
        }
        if (formData.pan_no && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_no.trim())) {
            newErrors.pan_no = 'Invalid PAN (e.g., ABCDE1234F)';
        }
        // GST is optional; when given it must be a valid GSTIN carrying the PAN.
        const gstErr = gstinError(formData.gstin_no, formData.pan_no);
        if (gstErr) newErrors.gstin_no = gstErr;
        if (!formData.ownership_share && formData.ownership_share !== 0) {
            newErrors.ownership_share = 'Ownership share is required';
        } else {
            const share = Number(formData.ownership_share);
            if (!Number.isInteger(share) || share < 1 || share > 100) {
                newErrors.ownership_share = 'Must be between 1 and 100';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const isIndividual = formData.entity_type === 'I';
        const payload = {
            id: member.flat_ownership_id || member.occupant_id,
            type: 'OWNER',
            entity_type: formData.entity_type,
            email: formData.email || undefined,
            phone: formData.phone,
            ownership_share: Number(formData.ownership_share),
        };
        if (isIndividual) {
            payload.first_name = formData.first_name;
            payload.last_name = formData.last_name;
            if (formData.gender) payload.gender = formData.gender;
            if (formData.date_of_birth) payload.date_of_birth = formData.date_of_birth;
        } else {
            payload.company_name = formData.company_name;
        }
        if (formData.flat_id !== 'all') payload.flat_id = formData.flat_id;
        if (formData.start_date) payload.start_date = formData.start_date;
        if (formData.secondary_email) payload.secondary_email = formData.secondary_email;
        if (formData.secondary_phone) payload.secondary_phone = formData.secondary_phone;
        if (formData.pan_no) payload.pan_no = formData.pan_no.trim().toUpperCase();
        if (formData.gstin_no) payload.gstin_no = formData.gstin_no.trim().toUpperCase();
        if (formData.address) payload.address = formData.address;
        if (formData.billing_address) payload.billing_address = formData.billing_address;
        if (formData.shipping_address) payload.shipping_address = formData.shipping_address;
        if (canPickRole && formData.role_id) payload.role_id = Number(formData.role_id);

        payload.onSuccess = () => {
            handleClose();
            if (onSuccess) onSuccess();
        };

        dispatch(updateOccupant(payload));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'visible', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
            <div className="tw-bg-white tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
                {/* Compact Header */}
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:account-edit-outline" className="tw-text-blue-600 tw-text-base" />
                        </div>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Edit Owner</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="tw-w-7 tw-h-7 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-transition-colors"
                    >
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                {/* Loading State */}
                {detailsLoading ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-blue-600 tw-border-t-transparent"></div>
                        <p className="tw-text-sm tw-text-gray-500 tw-mt-3">Loading details...</p>
                    </div>
                ) : (
                    /* Form */
                    <form onSubmit={onSubmit} className="tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
                        {/* Body scrolls (overflow-y-auto) so the footer with
                            the Update button stays fixed at the bottom of the
                            modal. Was `overflow-visible`, which pushed the
                            footer off the viewport on long content. */}
                        <div className="tw-px-4 tw-py-3 tw-space-y-3 tw-overflow-y-auto tw-flex-1 tw-min-h-0">
                            {/* Personal Info Section */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:account-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Personal Information</span>
                                </div>
                                {/* Entity type is fixed at Add Owner: switching Individual <-> Company
                                    would clear the name fields the member's login, ledgers and
                                    documents are built on. Shown, not editable. */}
                                <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
                                    <span className="tw-text-[11px] tw-font-medium tw-text-gray-600">Entity Type</span>
                                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-xs tw-font-medium tw-text-gray-700">
                                        <Icon icon={formData.entity_type === 'C' ? 'mdi:office-building-outline' : 'mdi:account-outline'} className="tw-text-sm tw-text-gray-500" />
                                        {ENTITY_TYPE_OPTIONS.find(opt => opt.value === formData.entity_type)?.label || '-'}
                                    </span>
                                    <span className="tw-text-[10px] tw-text-gray-400">Set when the owner was added - cannot be changed</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    {formData.entity_type === 'I' ? (
                                        <>
                                            <div>
                                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                                    First Name <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="first_name"
                                                    className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.first_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                                    placeholder="First Name"
                                                    value={formData.first_name}
                                                    onChange={handleChange}
                                                    maxLength={50}
                                                />
                                                {errors.first_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.first_name}</p>}
                                            </div>
                                            <div>
                                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                                    Last Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="last_name"
                                                    className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.last_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                                    placeholder="Last Name"
                                                    value={formData.last_name}
                                                    onChange={handleChange}
                                                    maxLength={50}
                                                />
                                                {errors.last_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.last_name}</p>}
                                            </div>
                                            <div>
                                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Gender</label>
                                                <SearchableDropdown
                                                    value={formData.gender}
                                                    onChange={handleDropdownChange('gender')}
                                                    options={GENDER_OPTIONS}
                                                    valueKey="value"
                                                    labelKey="label"
                                                    placeholder="Select Gender"
                                                    className="tw-w-full"
                                                />
                                            </div>
                                            <div>
                                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Date of Birth</label>
                                                <input
                                                    type="date"
                                                    name="date_of_birth"
                                                    max={today}
                                                    className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.date_of_birth ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                                    value={formData.date_of_birth}
                                                    onChange={handleChange}
                                                />
                                                {errors.date_of_birth && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.date_of_birth}</p>}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="tw-col-span-2">
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                                Company Name <span className="tw-text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="company_name"
                                                className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.company_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                                placeholder="Company Name"
                                                value={formData.company_name}
                                                onChange={handleChange}
                                                maxLength={100}
                                            />
                                            {errors.company_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.company_name}</p>}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Contact Info Section */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:phone-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Contact</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Email Address"
                                            value={formData.email}
                                            onChange={handleChange}
                                            maxLength={100}
                                        />
                                        {errors.email && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.email}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Secondary Email</label>
                                        <input
                                            type="email"
                                            name="secondary_email"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.secondary_email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Secondary Email"
                                            value={formData.secondary_email}
                                            onChange={handleChange}
                                            maxLength={100}
                                        />
                                        {errors.secondary_email && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.secondary_email}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                            Phone <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="phone"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Phone Number"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            maxLength={15}
                                        />
                                        {errors.phone && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.phone}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Tax Details Section */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:card-account-details-outline" className="tw-text-purple-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Tax Details</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">PAN Number</label>
                                        <input
                                            type="text"
                                            name="pan_no"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm tw-uppercase focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.pan_no ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="ABCDE1234F"
                                            value={formData.pan_no}
                                            onChange={handleChange}
                                            maxLength={10}
                                        />
                                        {errors.pan_no && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.pan_no}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                            GST Number <span className="tw-text-gray-400 tw-font-normal">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="gstin_no"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm tw-uppercase focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.gstin_no ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="22ABCDE1234F1Z5"
                                            value={formData.gstin_no}
                                            onChange={handleChange}
                                            maxLength={15}
                                        />
                                        {errors.gstin_no && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.gstin_no}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Addresses Section */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:map-marker-outline" className="tw-text-green-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Addresses</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Primary Address</label>
                                        <textarea
                                            name="address"
                                            rows={3}
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                            placeholder="Primary address"
                                            value={formData.address}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Billing Address</label>
                                        <textarea
                                            name="billing_address"
                                            rows={3}
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                            placeholder="Billing address"
                                            value={formData.billing_address}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div>
                                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600">Shipping Address</label>
                                            <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-600 tw-cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    name="same_as_billing"
                                                    checked={formData.same_as_billing}
                                                    onChange={handleChange}
                                                    className="tw-cursor-pointer"
                                                />
                                                Same as billing
                                            </label>
                                        </div>
                                        <textarea
                                            name="shipping_address"
                                            rows={3}
                                            disabled={formData.same_as_billing}
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-cursor-not-allowed"
                                            placeholder="Shipping address"
                                            value={formData.shipping_address}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Unit Info Section */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:home-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Unit Details</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Wing</label>
                                        <SearchableDropdown
                                            value={formData.wing_id}
                                            onChange={handleWingChange}
                                            options={wings}
                                            valueKey="wing_id"
                                            labelKey="title"
                                            placeholder="Select Wing"
                                            className="tw-w-full"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Flat</label>
                                        <SearchableDropdown
                                            value={formData.flat_id}
                                            onChange={handleFlatChange}
                                            options={modalFlats}
                                            valueKey="flat_id"
                                            labelKey="flat_number"
                                            placeholder="Select Flat"
                                            loading={flatsDropdownLoading}
                                            className="tw-w-full"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                            Ownership Share (%) <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            name="ownership_share"
                                            value={formData.ownership_share}
                                            onChange={handleChange}
                                            min={1}
                                            max={100}
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.ownership_share ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="e.g. 100"
                                        />
                                        {errors.ownership_share && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.ownership_share}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Start Date</label>
                                        <input
                                            type="date"
                                            name="start_date"
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                            value={formData.start_date}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {canPickRole && (
                                        <div className="tw-col-span-2">
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Login Role</label>
                                            <MemberRoleSelect
                                                value={formData.role_id}
                                                onChange={handleRoleChange}
                                                placeholder="Default: Resident"
                                                className="tw-w-full"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* Footer — flex-shrink-0 keeps it visible even
                            when the body pushes hard against the modal max-height. */}
                        <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={updateLoading}
                                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={updateLoading}
                                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50"
                            >
                                {updateLoading ? (
                                    <>
                                        <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:check" className="tw-text-base" />
                                        Update Owner
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Dialog>
    );
};

export default EditOwnerModal;
